import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { unparse } from 'papaparse';
import { apiAdmincompaniesGetCollection } from '@r/api/generated/client.ts';
import type {
  ApiAdmincompaniesGetCollectionParams,
  ApiAdmincompaniesGetCollectionStatusItem
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useCompanyColumns, useFilterFields } from '@admin/components/company/company-columns.tsx';
import { downloadCSV } from '@r/utils/utils.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { WorkspaceStepsInfoProcessor } from '@r/utils/workspaceSteps/workspace-steps-info-processor.ts';
import { isStepLogicWorkspace, isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import type { CompanyCompanyCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';

type CompanyCollection = CompanyCompanyCollectionReadModel & {
  siren?: string | null;
  tva?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  comment?: string | null;
};

enum StepEnum {}
enum TypologyEnum {}

function isStepEnum(value: string): value is keyof typeof StepEnum {
  return Object.keys(StepEnum).includes(value);
}

function isTypologyEnum(value: string): value is keyof typeof TypologyEnum {
  return Object.keys(TypologyEnum).includes(value);
}

export default function CompanyList() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);
  const user = useUser();
  const { workspace, isWorkspaceSuccess } = UseWorkspace(user.data);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const { sanitizedWorkspaceName } = useWorkspaceName();

  if (isWorkspaceSuccess && workspace && user.data) {
    const typologies = workspace.typologies;

    if (typologies) {
      const typologyEnum = typologies.reduce(
        (acc, typology) => {
          acc[typology.id ?? typology.name] = typology.name;
          return acc;
        },
        {} as Record<string, string>
      );

      Object.assign(TypologyEnum, typologyEnum);
    }

    //TODO Only working with "palier program"
    const { workspaceSteps } = WorkspaceStepsInfoProcessor(workspace, user.data);

    if (workspaceSteps) {
      const stepEnum = workspaceSteps.reduce(
        (acc, step, index) => {
          if (index === 0) {
            acc.null = t('company.no.step');
          }

          acc[step.id ?? step.name] = step.name;
          return acc;
        },
        {} as Record<string, string>
      );

      Object.assign(StepEnum, stepEnum);
    }
  }

  async function companiesListFiltersQuery(
    filters: {
      name: string;
      uniqueCode: string;
      siren: string;
      tva: string;
      addressLine1: string;
      city: string;
      countryAlpha3Values: string[];
      zipcode: string;
      status: ApiAdmincompaniesGetCollectionStatusItem[] | undefined;
      responsibleFullName: string;
      comment: string;
      acquiredPointsMin: string;
      acquiredPointsMax: string;
      balanceMin: string;
      balanceMax: string;
      registeredAtAfter: string;
      registeredAtBefore: string;
      pointCoeffMin: string;
      pointCoeffMax: string;
      commercialSortValue: 'asc' | 'desc' | undefined;
      balanceSortValue: 'asc' | 'desc' | undefined;
      lastStepReachedSortValue: 'asc' | 'desc' | undefined;
      typologySortValue: 'asc' | 'desc' | undefined;
      registeredAtSortValue: 'asc' | 'desc' | undefined;
      pointCoeffSortValue: 'asc' | 'desc' | undefined;
      acquiredPointsSortValue: 'asc' | 'desc' | undefined;
      steps: StepEnum[] | undefined;
      typologies: TypologyEnum[] | undefined;
    },
    page: number | null = null,
    limit: number | null = null
  ) {
    const {
      name,
      uniqueCode,
      siren,
      tva,
      addressLine1,
      city,
      countryAlpha3Values,
      zipcode,
      status,
      responsibleFullName,
      comment,
      acquiredPointsMin,
      acquiredPointsMax,
      balanceMin,
      balanceMax,
      registeredAtAfter,
      registeredAtBefore,
      pointCoeffMin,
      pointCoeffMax,
      commercialSortValue,
      balanceSortValue,
      lastStepReachedSortValue,
      typologySortValue,
      registeredAtSortValue,
      pointCoeffSortValue,
      acquiredPointsSortValue,
      steps,
      typologies
    } = filters;

    try {
      const params: ApiAdmincompaniesGetCollectionParams = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(name ? { name } : {}),
        ...(uniqueCode ? { uniqueCode } : {}),
        ...(siren ? { siren } : {}),
        ...(tva ? { tva } : {}),
        ...(addressLine1 ? { addressLine1 } : {}),
        ...(city ? { city } : {}),
        ...(countryAlpha3Values.length === 1
          ? { 'country.alpha3': countryAlpha3Values[0] }
          : countryAlpha3Values.length > 1
            ? { 'country.alpha3[]': countryAlpha3Values }
            : {}),
        ...(zipcode ? { zipcode } : {}),
        ...(status && status.length > 0 ? { 'status[]': status } : {}),
        ...(responsibleFullName ? { 'responsible.user.fullname': responsibleFullName } : {}),
        ...(comment ? { comment } : {}),
        ...(acquiredPointsMin ? { 'acquiredPoints[gte]': acquiredPointsMin } : {}),
        ...(acquiredPointsMax ? { 'acquiredPoints[lte]': acquiredPointsMax } : {}),
        ...(balanceMin ? { 'balance[gte]': balanceMin } : {}),
        ...(balanceMax ? { 'balance[lte]': balanceMax } : {}),
        ...(registeredAtAfter ? { 'registeredAt[after]': registeredAtAfter } : {}),
        ...(registeredAtBefore ? { 'registeredAt[before]': registeredAtBefore } : {}),
        ...(pointCoeffMin ? { 'pointCoeff[gte]': pointCoeffMin } : {}),
        ...(pointCoeffMax ? { 'pointCoeff[lte]': pointCoeffMax } : {}),
        ...(steps && steps.length > 0 ? { 'lastStepReached[]': steps.map(String) } : {}),
        ...(typologies && typologies.length > 0 && isTypologyConversion(workspaceConfigContext)
          ? { 'typology[]': typologies.map(String) }
          : {}),
        ...(commercialSortValue ? { 'order[commercial]': commercialSortValue } : {}),
        ...(balanceSortValue ? { 'order[balance]': balanceSortValue } : {}),
        ...(lastStepReachedSortValue ? { 'order[lastStepReached]': lastStepReachedSortValue } : {}),
        ...(typologySortValue ? { 'order[typology.name]': typologySortValue } : {}),
        ...(registeredAtSortValue ? { 'order[registeredAt]': registeredAtSortValue } : {}),
        ...(pointCoeffSortValue ? { 'order[pointCoeff]': pointCoeffSortValue } : {}),
        ...(acquiredPointsSortValue ? { 'order[acquiredPoints]': acquiredPointsSortValue } : {})
      };

      return await apiAdmincompaniesGetCollection(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/companies')) {
        showOrvalErrorToast(error);
      }
      throw error;
    }
  }

  function companiesListFilters() {
    const name = (columnFilters.find((filter) => filter.id === 'name')?.value as string) ?? '';

    const uniqueCode =
      (columnFilters.find((filter) => filter.id === 'uniqueCode')?.value as string) ?? '';
    const siren = (columnFilters.find((filter) => filter.id === 'siren')?.value as string) ?? '';
    const tva = (columnFilters.find((filter) => filter.id === 'tva')?.value as string) ?? '';
    const addressLine1 =
      (columnFilters.find((filter) => filter.id === 'addressLine1')?.value as string) ?? '';
    const city = (columnFilters.find((filter) => filter.id === 'city')?.value as string) ?? '';
    const countryFilter = columnFilters.find((filter) => filter.id === 'country.name');
    const countryAlpha3Values = Array.isArray(countryFilter?.value)
      ? (countryFilter?.value as string[])
      : [];
    const zipcode =
      (columnFilters.find((filter) => filter.id === 'zipcode')?.value as string) ?? '';

    const statusFilter = columnFilters.find((filter) => filter.id === 'status');
    const status = statusFilter?.value as ApiAdmincompaniesGetCollectionStatusItem[] | undefined;
    const responsibleFilter = columnFilters.find((filter) => filter.id === 'commercial');
    const responsibleFullName = (responsibleFilter?.value as string) ?? '';

    const comment =
      (columnFilters.find((filter) => filter.id === 'comment')?.value as string) ?? '';

    const acquiredPointsMin =
      (columnFilters.find((filter) => filter.id === 'acquiredPointsMin')?.value as string) ?? '';
    const acquiredPointsMax =
      (columnFilters.find((filter) => filter.id === 'acquiredPointsMax')?.value as string) ?? '';
    const balanceMin =
      (columnFilters.find((filter) => filter.id === 'balanceMin')?.value as string) ?? '';
    const balanceMax =
      (columnFilters.find((filter) => filter.id === 'balanceMax')?.value as string) ?? '';
    const registeredAtAfter =
      (columnFilters.find((filter) => filter.id === 'registeredAtAfter')?.value as string) ?? '';
    const registeredAtBefore =
      (columnFilters.find((filter) => filter.id === 'registeredAtBefore')?.value as string) ?? '';
    const pointCoeffMin =
      (columnFilters.find((filter) => filter.id === 'pointCoeffMin')?.value as string) ?? '';
    const pointCoeffMax =
      (columnFilters.find((filter) => filter.id === 'pointCoeffMax')?.value as string) ?? '';

    const sortValue = (id: string): 'asc' | 'desc' | undefined => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    const stepFilter = columnFilters.find((filter) => filter.id === 'lastStepReached');

    let steps: StepEnum[] | undefined;

    if (
      stepFilter &&
      Array.isArray(stepFilter.value) &&
      stepFilter.value.length > 0 &&
      isStepEnum(stepFilter.value[0])
    ) {
      steps = stepFilter.value as StepEnum[];
    }

    const typologyFilter = columnFilters.find((filter) => filter.id === 'typology');

    let typologies: TypologyEnum[] | undefined;

    if (
      typologyFilter &&
      Array.isArray(typologyFilter.value) &&
      typologyFilter.value.length > 0 &&
      isTypologyEnum(typologyFilter.value[0])
    ) {
      typologies = typologyFilter.value as TypologyEnum[];
    }

    return {
      name,
      uniqueCode,
      siren,
      tva,
      addressLine1,
      city,
      countryAlpha3Values,
      zipcode,
      status,
      responsibleFullName,
      comment,
      acquiredPointsMin,
      acquiredPointsMax,
      balanceMin,
      balanceMax,
      registeredAtAfter,
      registeredAtBefore,
      pointCoeffMin,
      pointCoeffMax,
      commercialSortValue: sortValue('commercial'),
      balanceSortValue: sortValue('balance'),
      lastStepReachedSortValue: sortValue('lastStepReached'),
      typologySortValue: sortValue('typology'),
      registeredAtSortValue: sortValue('registeredAt'),
      pointCoeffSortValue: sortValue('pointCoeff'),
      acquiredPointsSortValue: sortValue('acquiredPoints'),
      steps,
      typologies
    };
  }

  function getCompanyDataForExport(company: CompanyCollection) {
    // Remove new lines and tabs from the values
    const sanitize = (value: string | undefined) => value?.replace(/[\r\n\t]/g, '') ?? '';

    const base = [
      String(company.id),
      sanitize(company.name),
      company.uniqueCode,
      company.siren,
      company.tva,
      company.status ? t(company.status) : '',
      sanitize(company.addressLine1),
      sanitize(company.addressLine2 ?? undefined),
      sanitize(company.addressLine3 ?? undefined),
      sanitize(company.zipcode),
      sanitize(company.city),
      company.country?.name,
      company.acquiredPoints,
      company.balance
    ];

    const withStep = isStepLogicWorkspace(workspace)
      ? [...base, company.lastStepReached?.name]
      : base;

    const withTypologyOrCoeff = isTypologyConversion(workspaceConfigContext)
      ? [...withStep, company.typology?.name]
      : [...withStep, company.pointCoeff?.toLocaleString()];

    const withResponsible = [
      ...withTypologyOrCoeff,
      company.responsibles?.map((responsible) => responsible.user.fullname).join(', ') ?? '',
      company.responsibles?.map((responsible) => responsible.uniqueCode).join(', ') ?? ''
    ];

    return [
      ...withResponsible,
      sanitize(company.comment ?? undefined),
      company.registeredAt ? format(new Date(company.registeredAt), 'yyyy-MM-dd') : '',
      company.createdAt ? format(new Date(company.createdAt), 'yyyy-MM-dd') : ''
    ];
  }

  const companiesListQuery = useQuery({
    queryKey: ['companies_list'],
    queryFn: async () => {
      const companies = await companiesListFiltersQuery(companiesListFilters());

      if (companies.data) {
        for (let i = 0; i < companies.data.length; i++) {
          (companies.data[i] as CompanyCollection & { url?: string }).url =
            `/companies/${companies.data[i].id}`;
        }
      }

      return companies;
    }
  });

  async function exportData() {
    setIsExporting(true);

    let allData: CompanyCollection[] = [];
    const limit = 500;

    // Récupérer le nombre total de pages
    const totalPageCount = Math.ceil(
      (companiesListQuery.data?.pagination?.totalItems ?? 1) / limit
    );

    // Créer un tableau de promesses pour chaque page de données
    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      companiesListFiltersQuery(companiesListFilters(), i + 1, limit)
    );

    // Attendre que toutes les promesses soient résolues
    const pagesData = await Promise.all(promises);

    // Utiliser flatMap pour transformer et aplatir les données de chaque page en un seul tableau
    allData = pagesData.flatMap((pageData) => pageData.data);

    const pointsName = t('point.name_other');

    // Définir les noms des colonnes pour l'en-tête du CSV
    const common = [
      t('label.company.id'),
      t('label.company.name'),
      t('label.company.unique.code'),
      t('label.company.siren'),
      t('label.company.tva'),
      t('label.company.status'),
      t('label.company.address.line1'),
      t('label.company.address.line2'),
      t('label.company.address.line3'),
      t('label.company.zipcode'),
      t('label.company.city'),
      t('label.company.country'),
      `${pointsName.charAt(0).toUpperCase() + pointsName.slice(1)} ${t('common.acquired')}`,
      t('common.balance')
    ];

    const withStepHeader = isStepLogicWorkspace(workspace)
      ? [...common, t('label.company.current.step')]
      : common;

    const withTypologyOrCoeffHeader = isTypologyConversion(workspaceConfigContext)
      ? [...withStepHeader, t('label.company.typology')]
      : [...withStepHeader, t('label.company.point.coefficient')];

    const fields = [
      ...withTypologyOrCoeffHeader,
      t('label.company.responsible'),
      t('label.company.responsible.unique.code'),
      t('label.company.comment'),
      t('label.company.registered.at'),
      t('label.company.created.at')
    ];

    // Récupérer les données de chaque société pour l'export
    const companiesData = allData.map((company) => getCompanyDataForExport(company));

    const csv = unparse({
      fields: fields,
      data: companiesData
    });

    const currentDate = new Date();

    // Générer le nom du fichier CSV
    const baseFileName = `companies-export-${format(currentDate, 'yyyy-MM-dd-HHmmss')}.csv`;
    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;

    downloadCSV(csv, fileName);
    setIsExporting(false);
  }

  const filterFields = useFilterFields({
    stepFilters: StepEnum,
    typologyFilters: TypologyEnum
  });

  const companiesTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur:
    // chaque admin retrouve setres propres fils/pagination sans impacter les autres.
    () => getAdminTablePersistenceKey('companies', user.data?.id),
    [user.data?.id]
  );

  const { table } = useDataTable({
    data: companiesListQuery.data?.data ?? [],
    columns: useCompanyColumns(workspaceConfigContext),
    filterFields,
    pageCount: companiesListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Active la persistance locale de l'etat de table (filtres, tri, pagination).
      key: companiesTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      companiesListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.companies.title')}</span>

      {companiesListQuery.isLoading && (
        <DataTableSkeleton
          columnCount={5}
          cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
        />
      )}

      {companiesListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />

          <DataTable table={table} totalItems={companiesListQuery.data?.pagination?.totalItems} />
        </>
      )}
    </>
  );
}
